import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import posthog from 'posthog-js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Play } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DateRangeSelect } from '../DateRangeSelect'
import { FieldLabel } from '../FieldLabel'
import { TokenInput } from '../TokenInput'
import { useNetwork } from '../../contexts/NetworkContext'
import { useResolvedRange } from '../../hooks/useResolvedRange'
import { dateRangeLabel, getDateRange, type DateRange } from '@/services/stats/statsRange'
import { logPosthogInfo } from '@/services/core/posthogLog'
import { ReportsApi, REPORT_TARGETS, type ReportTarget } from '@/services/reports/reportsApi'

const posthogConfigured = Boolean(
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
)

interface RunReportDialogProps {
  target: ReportTarget
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Runs one report, now. Everything the report engine needs beyond the template
 * is a period, so that is the only real choice on the dialog — and it offers
 * the object's own flight, which is what a campaign report is almost always
 * about.
 */
export function RunReportDialog({ target, open, onOpenChange }: RunReportDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'

  const [templateId, setTemplateId] = useState('')
  const [range, setRange] = useState<DateRange>('flight')
  const [name, setName] = useState('')
  const [emails, setEmails] = useState<string[]>([])
  // Set once the person edits the name, so a later template change stops
  // overwriting what they typed.
  const [nameEdited, setNameEdited] = useState(false)

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['report-templates', target.type],
    queryFn: () => ReportsApi.getTemplates(target.type),
    enabled: open,
  })

  const template = templates.find((item) => item.id === templateId)
  const flight = { startDate: target.startDate, endDate: target.endDate }
  const resolved = useResolvedRange(range, flight)

  useEffect(() => {
    if (!open) return
    if (!templateId && templates.length > 0) setTemplateId(templates[0].id)
  }, [open, templates, templateId])

  useEffect(() => {
    if (nameEdited) return
    const parts = [target.name, template?.name].filter(Boolean)
    setName(parts.join(' — '))
  }, [target.name, template?.name, nameEdited])

  const mutation = useMutation({
    mutationFn: () => {
      const { startDate, endDate } = getDateRange(resolved, timeZone)
      return ReportsApi.run({
        name,
        templateId,
        parameters: { [REPORT_TARGETS[target.type].parameter]: target.id, startDate, endDate },
        recipientEmailAddresses: emails,
      })
    },
    onSuccess: () => {
      if (posthogConfigured) posthog.capture('scheduled_report_queued', {
        target_type: target.type,
        date_range: range,
        recipient_count: emails.length,
      })
      logPosthogInfo('scheduled report queued', {
        event: 'scheduled_report_queued',
        target_type: target.type,
        date_range: range,
        recipient_count: emails.length,
      })
      toast.success(t('reports.queued'))
      queryClient.invalidateQueries({ queryKey: ['report-executions', target.type, target.id] })
      close()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const close = () => {
    onOpenChange(false)
    // Reset so the next report starts from the defaults rather than from
    // whatever the last one happened to be.
    setEmails([])
    setNameEdited(false)
    setRange('flight')
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('reports.runReport')}</DialogTitle>
          <DialogDescription>{t('reports.runReportHelp')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="p-field">
            <FieldLabel>{t('reports.template')}</FieldLabel>
            <Select value={templateId} onValueChange={setTemplateId} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder={t('reports.templatePlaceholder')}>
                  {template?.name ?? null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {templates.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isLoading && templates.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">{t('reports.noTemplates')}</p>
            )}
          </div>

          <div className="p-field">
            <FieldLabel hint={dateRangeLabel(resolved, t)}>{t('reports.period')}</FieldLabel>
            <DateRangeSelect value={range} onChange={setRange} flight={flight} className="w-full" />
          </div>

          <div className="p-field">
            <FieldLabel>{t('reports.name')}</FieldLabel>
            <Input
              value={name}
              onChange={(e) => {
                setNameEdited(true)
                setName(e.target.value)
              }}
            />
          </div>

          <div className="p-field">
            <FieldLabel tooltip={t('reports.emailRecipientsHelp')}>
              {t('reports.emailRecipients')}
            </FieldLabel>
            <TokenInput
              value={emails}
              onChange={setEmails}
              lowercase
              placeholder={t('reports.emailPlaceholder')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!templateId || !name.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            <Play className="size-4" />
            {t('reports.run')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
